import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { resetGetSolar, setRecommendationDetails } from "../../../stores/slices/get-solar-slice";
import styles from "./get-solar.module.scss";
import CustomerPageLayout from "../customer-page-layout/CustomerPageLayout";
import { EarthIcon, SolarSunIcon } from "../../../assets/icons";
import { ChoosePaymentPlan } from "./components/ChoosePaymentPlan";
import { GetSolarViewState, NextStepData, VIEW_FLOW } from "./get-solar.types";
import { EmploymentInformation } from "./components/credit-check/zambia/employment-information/employment-information";
import { EnergyRequirements } from "./components/energy-requirements/EnergyRequirements";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../stores/store";
import { IdentityInformation } from "./components/credit-check/zambia/identity-information/identity-information";
import { ContactInformation } from "./components/credit-check/zambia/contact-information/contact-information";
import { MeterInformation } from "./components/credit-check/zambia/meter-information/meter-information";
import { Installation } from "./components/installation/Installation";
import { Confirmation } from "./components/confirmation/Confirmation";
import { useLazyGetSolarRecommendationQuery } from "../../../service/solar-recommendation-service";
import { RecommendationRequestStatus } from "../../../service/contract/solar-recommendation";
import { ProgressPage } from "./components/progress-page/progress-page";
import { CustomerLoader, SuccessScreen } from "../components";
import { RecommendationsPage } from "./components/tailored-recommendation/recommendations-page/recommendations-page";
import { PayAsYouGoInfoPage } from "./components/tailored-recommendation/pay-as-you-go-info-page/pay-as-you-go-info-page";
import { UpdateDeliveryAddressRequest } from "../../../model/delivery-address";
import { useGetOrderPageQuery, getOrderStatusLabel } from "../../../service/customer/order-workflow-service";
import { ApplicationRoutes } from "../../../routes";
import { BillingInformationForm } from "./components/billing-information/billing-information-form";
import { BillingInformationPage } from "./components/billing-information/billing-information-page";

export const GetSolarPage: React.FC = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const userInfo = useSelector((state: RootState) => state.user);
	const recommendationDetails = useSelector((state: RootState) => state.getSolar.recommendationDetails);

	const [getRecommendationDetails, {isLoading, isFetching}] = useLazyGetSolarRecommendationQuery();
	const { data: ordersData, isLoading: isOrdersLoading } = useGetOrderPageQuery({ page: 1, pageSize: 1 }, { refetchOnMountOrArgChange: true });
	const latestOrder = ordersData?.items?.[0];

	const [currentView, setCurrentView] = useState<GetSolarViewState>("landing");
	const [nextStepData, setNextStepData] = useState<NextStepData>({ countryCode: userInfo.countryCode });
	const [energyHeaderTitle, setEnergyHeaderTitle] = useState("Tell Us Your Energy Requirements");
	const energyBackRef = useRef<() => void>(() => {});
	const [addressToEdit, setAddressToEdit] = useState<UpdateDeliveryAddressRequest | undefined>();

	useEffect(() => {
		const fetchRecommendationDetails = async () => {
			try {
				const details = await getRecommendationDetails().unwrap();
				dispatch(setRecommendationDetails(details));
				setNextStepData({ paymentPlan: details.assessmentDetails?.paymentPlan });
			} catch (error) {
				dispatch(setRecommendationDetails(null));
			}
		};
		fetchRecommendationDetails();
	}, [dispatch, getRecommendationDetails]);

	const onStartNowClick = () => {
		// 1. If recommendation exists and status is pending, navigate to tailored recommendation page
		if (recommendationDetails != null && recommendationDetails.status === RecommendationRequestStatus.Pending) {
			setCurrentView("tailored-recommendations-page");
			return;
		}
		// 2. If recommendation exists and status is not pending, open "Recommendation in progress" screen
		if (recommendationDetails != null && recommendationDetails.status !== RecommendationRequestStatus.Pending) {
			setCurrentView("progress-page");
			return;
		}
		
		// 3. If no recommendation found (404), start the payment plan flow
		goToNextView();
	};

	const goToNextView = useCallback(() => {
		const nextView = VIEW_FLOW[currentView]?.next;
		if (nextView) {
			setCurrentView(nextView(nextStepData));
		}
	}, [currentView, nextStepData]);

	const goToPreviousView = useCallback(() => {
		const prevView = VIEW_FLOW[currentView]?.prev;
		if (prevView) {
			setCurrentView(prevView(nextStepData));
		}
	}, [currentView, nextStepData]);

	const handleSetNextStepData = useCallback((data: Partial<NextStepData>) => {
		setNextStepData(prev => ({ ...prev, ...data }));
	}, []);

	const handleEnergyHeaderChange = useCallback((title: string, onBack: () => void) => {
		setEnergyHeaderTitle(title);
		energyBackRef.current = onBack;
	}, []);

	const layoutProps = useMemo(() => {
		switch (currentView) {
			case "payment-plan":
				return {
					title: "Choose A Payment Plan",
					showBackButton: true,
					onBack: goToPreviousView,
				};
			case "energy-requirements":
				return {
					title: energyHeaderTitle,
					showBackButton: true,
					onBack: () => energyBackRef.current(),
				};
			case "employment-information-zambia":
				return {
					title: "Employment Information",
					showBackButton: true,
					onBack: goToPreviousView,
				};
			case "identity-information-zambia":
				return {
					title: "Identity Information",
					showBackButton: true,
					onBack: goToPreviousView,
				};
			case "contact-information-zambia":
				return {
					title: "Contact Information",
					showBackButton: true,
					onBack: goToPreviousView,
				};
			case "meter-information-zambia":
				return {
					title: "Financial Information",
					showBackButton: true,
					onBack: goToPreviousView,
				};
			case "installation":
				return {
					title: "Installation",
					showBackButton: true,
					onBack: goToPreviousView,
				};
			case "confirmation":
				return {
					title: "Confirmation",
					showBackButton: true,
					onBack: goToPreviousView,
				};
			case "progress-page":
				return {
					title: "Solar Recommendation Updates",
					showBackButton: true,
					onBack: goToPreviousView,
				};
			case "tailored-recommendations-page":
				return {
					title: "Recommendations",
					showBackButton: true,
					onBack: goToPreviousView,
				};
			case "pay-as-you-go-info-page":
				return {
					title: "Recommendation",
					showBackButton: true,
					onBack: goToPreviousView,
				};
			case "billing-information-page":
				return {
					title: "Billing Information",
					showBackButton: true,
					onBack: goToPreviousView,
				};
			case "billing-information-form":
				return {
					title: addressToEdit ? "Edit Address & Contact" : "Add Address & Contact",
					showBackButton: true,
					onBack: goToPreviousView,
				};
			default:
				return {
					title: "Get Solar",
					showBackButton: false,
				};
		}
	}, [currentView, goToPreviousView, energyHeaderTitle, addressToEdit]);

	if (isLoading || isFetching || isOrdersLoading) {
		return (
			<CustomerPageLayout
			{...layoutProps}
			icon={<EarthIcon />}
			>
				<CustomerLoader
					title={"Loading Solar Recommendation"}
					subtitle="Please wait while we fetch your information..."/>
			</CustomerPageLayout>
		);
	}

	return (
		<CustomerPageLayout
			{...layoutProps}
			icon={<EarthIcon />}
		>
			{currentView === "landing" && (
				<div className={styles.pageContainer}>
					<div className={styles.subtitle}>
						Explore our solar packages and find the perfect fit for your energy needs.
					</div>
					<div className={styles.heroBanner}>
						<div className={styles.heroContent}>
							<span className={styles.heroLabel}>Find the best package</span>
							<h2 className={styles.heroHeading}>Tell us about your power needs</h2>
							<button className={styles.startNowButton} onClick={onStartNowClick}>
								{recommendationDetails?.status === RecommendationRequestStatus.Pending
									? "View Recommendation"
									: "Start Now"}
							</button>
						</div>
						<div className={styles.heroIllustration}>
							<SolarSunIcon />
						</div>
					</div>

					{latestOrder && (
						<div
							className={styles.solarJourneyBanner}
							onClick={() => navigate(`/solar-journey/${latestOrder.details.id}`)}
						>
							<div className={styles.solarJourneyContent}>
								<span className={styles.solarJourneyLabel}>
									{latestOrder.details.statusDetails?.status === 5 ? "Completed Order"
										: [6, 8, 9, 11, 12].includes(latestOrder.details.statusDetails?.status) ? "Failed Order"
										: "Active Order"}
								</span>
								<span className={styles.solarJourneyTitle}>
									{latestOrder.details.productDetails?.productName || "Your Solar Journey"}
								</span>
								<span className={styles.solarJourneyStatus}>
									{getOrderStatusLabel(latestOrder.details.statusDetails?.status)}
								</span>
							</div>
							<button className={styles.startNowButton}>
								View Solar Journey
							</button>
						</div>
					)}
				</div>
			)}

			{currentView === "payment-plan" && (
				<ChoosePaymentPlan onCancel={goToPreviousView} onContinue={goToNextView}/>
			)}

			{currentView === "employment-information-zambia" && (
				<EmploymentInformation onCancel={goToPreviousView} onContinue={goToNextView} setNextStepData={handleSetNextStepData} />
			)}

			{currentView === "identity-information-zambia" && (
				<IdentityInformation onCancel={goToPreviousView} onContinue={goToNextView} />
			)}

			{currentView === "contact-information-zambia" && (
				<ContactInformation onCancel={goToPreviousView} onContinue={goToNextView} />
			)}

			{currentView === "meter-information-zambia" && (
				<MeterInformation onCancel={goToPreviousView} onContinue={goToNextView} />
			)}

			{currentView === "energy-requirements" && (
				<EnergyRequirements
					onCancel={goToPreviousView}
					onContinue={goToNextView}
					onHeaderChange={handleEnergyHeaderChange}
				/>
			)}

			{currentView === "installation" && (
				<Installation onCancel={goToPreviousView} onSave={goToNextView} />
			)}

			{currentView === "confirmation" && (
				<Confirmation onCancel={goToPreviousView} onConfirm={() => {
					dispatch(resetGetSolar());
					navigate("/dashboard");
				}} />
			)}

			{currentView === "progress-page" && (
				<ProgressPage />
			)}

			{currentView === "tailored-recommendations-page" && (
				<RecommendationsPage onContinue={goToNextView} />
			)}

			{currentView === "pay-as-you-go-info-page" && (
				<PayAsYouGoInfoPage onContinue={goToNextView} />
			)}

			{currentView === "billing-information-page" && (
				<BillingInformationPage
					onCancel={goToPreviousView}
					onAddAddress={() => {
						setAddressToEdit(undefined);
						goToNextView();
					}}
					onEditAddress={(address) => {
						setAddressToEdit({deliveryAddressId: address.id, ...address});
						goToNextView();
					}}
					onOrderSuccess={() => setCurrentView("thank-you-page")}
				/>
			)}

			{currentView === "billing-information-form" && (
				<BillingInformationForm
					onCancel={goToPreviousView}
					onSaved={() => {
						setAddressToEdit(undefined);
						goToPreviousView();
					}}
					addressToEdit={addressToEdit}
				/>
			)}

			{currentView === "thank-you-page" && (
				<SuccessScreen
					title={"Thank you for completing your information verification."}
					buttonText="Go Home"
					subtitle="A Representative will be in touch with you shortly to schedule a Physical Assessment"
					onContinue={() => {
						dispatch(resetGetSolar());
						navigate('/dashboard');
					}}
				/>
			)}
		</CustomerPageLayout>
	);
};
